import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { firebaseAuth, firestore } from '../firebase';
import { addToCart, saveShippingAddress } from '../Actions/cartActions';
import { useToast } from '../Context/ToastContext';
import './DeliveryScreen.css';

export default function DeliveryScreen() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error: showError, info } = useToast();

  // Always scroll to top when this page loads
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, []);

  const userSignin = useSelector((state) => state.userSignin);
  const { userInfo } = userSignin || {};
  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);

  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [qty, setQty] = useState(1);

  // Add Address Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    type: 'Home',
    name: '',
    mobileNo: '',
    alternateMobileNo: '',
    fullAddress: '',
    locality: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
  });

  // Determine the single item being purchased via Buy Now
  const checkoutItem = useMemo(() => {
    try {
      const raw = localStorage.getItem('checkoutItem');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }, []);

  const targetProductId = checkoutItem?.productId || null;
  const [fallbackProduct, setFallbackProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const item = useMemo(() => {
    if (!targetProductId) return null;
    return cartItems.find((it) => it.product === targetProductId) || null;
  }, [cartItems, targetProductId]);

  // Sync qty with cart item whenever it changes (ignore checkoutItem; avoid reverting the user's new selection)
  useEffect(() => {
    const minQ = Math.max(1, Number((item || fallbackProduct)?.min_quantity) || 1);
    const itemQty = Number(item?.qty);
    if (Number.isFinite(itemQty) && itemQty !== qty) {
      setQty(Math.max(itemQty, minQ));
    } else if (!Number.isFinite(itemQty) && (!Number.isFinite(Number(qty)) || qty < minQ)) {
      setQty(minQ);
    }
  }, [item?.qty, (item || fallbackProduct)?.min_quantity]);

  // If cart has this item but qty is below min, correct it to min and persist
  useEffect(() => {
    (async () => {
      if (!item) return;
      const minQ = Math.max(1, Number(item.min_quantity) || 1);
      const currentQ = Number(item.qty) || 1;
      if (currentQ < minQ && targetProductId) {
        const newQ = minQ;
        setQty(newQ);
        try { await dispatch(addToCart(targetProductId, newQ)); } catch(_) {}
        try {
          const raw = localStorage.getItem('checkoutItem');
          const parsed = raw ? JSON.parse(raw) : {};
          if (parsed && parsed.productId === targetProductId) {
            parsed.qty = newQ;
            localStorage.setItem('checkoutItem', JSON.stringify(parsed));
          }
        } catch(_) {}
      }
    })();
  }, [item, targetProductId, dispatch]);

  // Add Address submit handler (top-level so JSX can access it)
  const handleAddAddress = async (e) => {
    e?.preventDefault?.();
    // Basic validation
    if (!newAddress.name || !newAddress.mobileNo || !newAddress.fullAddress || !newAddress.city || !newAddress.pincode) {
      showError('Please fill all required fields');
      return;
    }
    try {
      setAddingAddress(true);
      const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
      if (!uid) throw new Error('Not signed in');

      const myAddressesRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_ADDRESSES');
      const myAddressesSnap = await getDoc(myAddressesRef);
      const existing = myAddressesSnap.exists() ? (myAddressesSnap.data() || {}) : { list_size: 0 };
      const currentSize = Number(existing.list_size) || 0;
      const newIndex = currentSize + 1;

      // Build updates in Android-compatible schema and mark new as selected
      const updates = {
        [`pincode_${newIndex}`]: newAddress.pincode,
        [`flatNo_${newIndex}`]: newAddress.fullAddress,
        [`locality_${newIndex}`]: newAddress.locality || '',
        [`city_${newIndex}`]: newAddress.city,
        [`landmark_${newIndex}`]: newAddress.landmark || '',
        [`name_${newIndex}`]: newAddress.name,
        [`mobileNo_${newIndex}`]: newAddress.mobileNo,
        [`alternateMobileNo_${newIndex}`]: newAddress.alternateMobileNo || '',
        [`state_${newIndex}`]: newAddress.state || '',
        [`type_${newIndex}`]: newAddress.type || 'Home',
        [`selected_${newIndex}`]: true,
        list_size: newIndex,
      };
      // Unselect previous selected
      addresses.forEach((addr) => {
        updates[`selected_${addr.id}`] = false;
      });

      if (!myAddressesSnap.exists()) {
        await setDoc(myAddressesRef, { list_size: 0 });
      }
      await updateDoc(myAddressesRef, updates);

      // Update local state and select
      const created = {
        id: String(newIndex),
        pincode: newAddress.pincode,
        flatNo: newAddress.fullAddress,
        locality: newAddress.locality || '',
        city: newAddress.city,
        landmark: newAddress.landmark || '',
        name: newAddress.name,
        mobileNo: newAddress.mobileNo,
        alternateMobileNo: newAddress.alternateMobileNo || '',
        state: newAddress.state || '',
        type: newAddress.type || 'Home',
        selected: true,
      };
      const updated = addresses.map(a => ({ ...a, selected: false })).concat(created);
      setAddresses(updated);
      setSelectedAddressId(String(newIndex));
      setShowAddModal(false);
      setNewAddress({ type: 'Home', name: '', mobileNo: '', alternateMobileNo: '', fullAddress: '', locality: '', city: '', state: '', pincode: '', landmark: '' });
      success('Address added successfully!');
    } catch (e) {
      console.error('Add address failed:', e);
      showError('Failed to add address');
    } finally {
      setAddingAddress(false);
    }
  };

  // If Buy Now didn't finish adding to cart in time, fetch product for display
  useEffect(() => {
    const fetchProduct = async () => {
      if (!targetProductId || item) return;
      try {
        setProductLoading(true);
        const snap = await getDoc(doc(firestore, 'PRODUCTS', targetProductId));
        if (snap.exists()) {
          const data = snap.data();
          setFallbackProduct({
            product: data.id,
            product_title: data.product_title,
            category: data.category,
            product_image_1: data.product_image_1,
            product_price: data.product_price,
            cutted_price: data.cutted_price,
            stock_quantity: data.stock_quantity,
            brand: data.brand,
            seller_id: data.seller_id,
            max_quantity: data.max_quantity,
            min_quantity: data.min_quantity || 1,
            average_rating: data.average_rating,
            total_ratings: data.total_ratings,
            product_description: data.product_description,
            COD: typeof data.COD === 'boolean' ? data.COD : !!data.cod_available,
            // Normalize variant arrays from multiple possible field names
            available_sizes: Array.isArray(data.available_sizes)
              ? data.available_sizes
              : (Array.isArray(data.available_size)
                  ? data.available_size
                  : (Array.isArray(data.sizes) ? data.sizes : undefined)),
            available_colors: Array.isArray(data.available_colors)
              ? data.available_colors
              : (Array.isArray(data.availableColors)
                  ? data.availableColors
                  : (Array.isArray(data.colors) ? data.colors : undefined)),
            qty: checkoutItem?.qty || 1,
          });
        }
      } catch (e) {
        console.error('Failed to fetch product for checkout:', e);
      }
      finally {
        setProductLoading(false);
      }
    };
    fetchProduct();
  }, [targetProductId, item, checkoutItem]);

  // Ensure the product exists in cart so qty selector updates cart state correctly
  const ensuredCartRef = useRef(false);
  useEffect(() => {
    (async () => {
      try {
        if (!ensuredCartRef.current && targetProductId && !item) {
          ensuredCartRef.current = true;
          const minQ = Math.max(1, Number(fallbackProduct?.min_quantity) || 1);
          const initialQty = Math.max(Number(checkoutItem?.qty || qty || 1), minQ);
          await dispatch(addToCart(targetProductId, initialQty));
        }
      } catch (_) {}
    })();
  }, [targetProductId, item, dispatch]);

  // Ensure user is signed in
  useEffect(() => {
    const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
    if (!uid) {
      history.push('/signin?redirect=shipping');
    }
  }, [userInfo, history]);

  // Load addresses from USERS/{uid}/USER_DATA/MY_ADDRESSES
  useEffect(() => {
    const loadAddresses = async () => {
      try {
        setLoadingAddresses(true);
        const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
        if (!uid) return;
        const addrDocRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_ADDRESSES');
        const snap = await getDoc(addrDocRef);
        if (!snap.exists()) {
          setAddresses([]);
          setSelectedAddressId('');
          return;
        }
        const data = snap.data() || {};
        const size = Number(data.list_size) || 0;
        const list = [];
        let defaultId = '';
        for (let i = 1; i <= size; i++) {
          const idx = String(i);
          const addr = {
            id: idx,
            pincode: data[`pincode_${idx}`] || '',
            flatNo: data[`flatNo_${idx}`] || '',
            locality: data[`locality_${idx}`] || '',
            city: data[`city_${idx}`] || '',
            landmark: data[`landmark_${idx}`] || '',
            name: data[`name_${idx}`] || '',
            mobileNo: data[`mobileNo_${idx}`] || '',
            alternateMobileNo: data[`alternateMobileNo_${idx}`] || '',
            state: data[`state_${idx}`] || '',
            type: data[`type_${idx}`] || 'Home',
            selected: Boolean(data[`selected_${idx}`]) || false,
          };
          list.push(addr);
          if (addr.selected) defaultId = idx;
        }
        setAddresses(list);
        setSelectedAddressId(defaultId || (list[0]?.id || ''));
      } catch (e) {
        console.error('Failed to load addresses:', e);
        setAddresses([]);
        setSelectedAddressId('');
      } finally {
        setLoadingAddresses(false);
      }
    };
    loadAddresses();
  }, [userInfo]);

  // Update qty via Redux when changed
  const onQtyChange = async (newQty) => {
    if (!targetProductId) return;
    try {
      const q = Number(newQty);
      setQty(q);
      await dispatch(addToCart(targetProductId, q));
      try {
        const data = localStorage.getItem('checkoutItem');
        if (data) {
          const parsed = JSON.parse(data);
          parsed.qty = q;
          localStorage.setItem('checkoutItem', JSON.stringify(parsed));
        }
      } catch (_) {}
    } catch (_) {}
  };

  const proceedToPayment = async () => {
    try {
      if (!selectedAddressId) {
        showError('Please select an address to continue');
        return;
      }
      const chosen = addresses.find((a) => a.id === selectedAddressId);
      if (!chosen) {
        showError('Invalid address selection');
        return;
      }

      // Persist selection back to Firestore (set selected flags)
      try {
        setAddressSaving(true);
        const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
        const addrDocRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_ADDRESSES');
        const updates = {};
        addresses.forEach((a) => {
          updates[`selected_${a.id}`] = a.id === selectedAddressId;
        });
        await updateDoc(addrDocRef, updates);
      } catch (e) {
        console.warn('Skipping address selection persistence:', e?.message || e);
      } finally {
        setAddressSaving(false);
      }

      // Save shipping address into Redux store for downstream screens
      dispatch(
        saveShippingAddress({
          name: chosen.name,
          landmark: chosen.landmark,
          flatNo: chosen.flatNo,
          locality: chosen.locality,
          city: chosen.city,
          state: chosen.state,
          pinCode: chosen.pincode,
          mobileNo: chosen.mobileNo,
          alternateMobileNo: chosen.alternateMobileNo,
          type: chosen.type,
        })
      );

      // Persist final quantity for the selected item so Payment/PlaceOrder read it reliably
      try {
        const raw = localStorage.getItem('checkoutItem');
        const parsed = raw ? JSON.parse(raw) : {};
        if (parsed && parsed.productId === targetProductId) {
          parsed.qty = effectiveQty;
          localStorage.setItem('checkoutItem', JSON.stringify(parsed));
        }
      } catch (_) {}

      // Optionally store variant selections for later use
      try {
        localStorage.setItem(
          'checkoutMeta',
          JSON.stringify({ size: size || null, color: color || null })
        );
      } catch (_) {}

      history.push('/payment');
    } catch (e) {
      console.error('Proceed to payment failed:', e);
      showError('Something went wrong. Please try again.');
    }
  };

  const renderQtySelect = (p) => {
    const minQ = Math.max(1, Number(p.min_quantity) || 1);
    const maxByStock = Math.max(1, Number(p.stock_quantity) || 1);
    const maxQ = Math.max(1, Number(p.max_quantity) || 1);
    const allowedMax = Math.max(minQ, Math.min(maxQ, maxByStock));
    const currentQty = Math.min(Math.max(Number(qty) || minQ, minQ), allowedMax);

    const options = [];
    for (let q = minQ; q <= allowedMax; q++) {
      options.push(
        <option key={q} value={q}>
          {q}
        </option>
      );
    }

    return (
      <select
        value={currentQty}
        onChange={(e) => onQtyChange(e.target.value)}
        className="qty-select"
      >
        {options}
      </select>
    );
  };

  const displayItem = item || fallbackProduct;
  const hasItem = Boolean(displayItem);
  const minQtyForItem = hasItem ? Math.max(1, Number(displayItem.min_quantity) || 1) : 1;
  const effectiveQty = hasItem ? Math.max(Number(qty) || minQtyForItem, minQtyForItem) : 1;

  return (
    <div className="delivery-page row top">
      {/* Make Order Summary wider: col-2 for summary, col-1 for addresses */}
      <div className="col-1">
        <div className="card card-body">
          <div className="address-header-row">
            <h2>Select Delivery Address</h2>
            <button className="add-address-btn" type="button" onClick={() => setShowAddModal(true)}>
              <i className="fas fa-plus"></i>
              Add Address
            </button>
          </div>
          {loadingAddresses ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading your addresses…</div>
            </div>
          ) : addresses.length === 0 ? (
            <div>
              <p>No saved addresses found.</p>
              <button className="primary" type="button" onClick={() => setShowAddModal(true)}>
                Add Address
              </button>
            </div>
          ) : (
            <ul className="address-list">
              {addresses.map((a) => (
                <li key={a.id} className={`address-item ${selectedAddressId === a.id ? 'selected' : ''}`}>
                  <label className="address-radio">
                    <input
                      type="radio"
                      name="address"
                      value={a.id}
                      checked={selectedAddressId === a.id}
                      onChange={() => setSelectedAddressId(a.id)}
                    />
                    <div className="address-content fit">
                      <div className="address-header">
                        <span className="address-name">{a.name || '—'}</span>
                        <span className="address-type">{a.type}</span>
                      </div>
                      <div className="address-lines">
                        <div>{a.flatNo}</div>
                        {a.locality ? (<div>{a.locality}</div>) : null}
                        <div>
                          {a.city}{a.state ? `, ${a.state}` : ''}{a.pincode ? ` - ${a.pincode}` : ''}
                        </div>
                        <div className="address-phones">Mob: +91-{a.mobileNo}{a.alternateMobileNo ? ` / ${a.alternateMobileNo}` : ''}</div>
                      </div>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="col-2">
        <div className="card card-body">
          <div className="summary-header-row">
            <h2>Order Summary</h2>
            {hasItem && (
              <div className={`chip ${displayItem?.COD || displayItem?.cod_available ? 'chip-green' : 'chip-red'}`}>
                {displayItem?.COD || displayItem?.cod_available ? 'COD Available' : 'COD Not Available'}
              </div>
            )}
          </div>
          {!hasItem ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading item…</div>
            </div>
          ) : (
            <div className="summary-item">
              <div className="summary-row">
                <img
                  src={displayItem.product_image_1}
                  alt={displayItem.product_title}
                  className="summary-thumb"
                  onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
                />
                <div className="summary-info">
                  <div className="title">{displayItem.product_title}</div>
                  {displayItem.brand && (
                    <div className="meta">Brand: {displayItem.brand}</div>
                  )}
                  <div className="price-row">
                    {displayItem.cutted_price && Number(displayItem.cutted_price) > Number(displayItem.product_price) && (
                      <span className="mrp">₹ {displayItem.cutted_price}/-</span>
                    )}
                    <span className="price-now">₹ {displayItem.product_price}/-</span>
                  </div>
                </div>
              </div>

              <div className="selectors" style={{ marginTop: 12 }}>
                <div className="selector">
                  <label>Quantity</label>
                  {renderQtySelect(displayItem)}
                </div>
                <div className="selector">
                  <label>Size {Array.isArray(displayItem?.available_sizes) && displayItem.available_sizes.length ? '' : '(optional)'}</label>
                  {Array.isArray(displayItem?.available_sizes) && displayItem.available_sizes.length ? (
                    <select value={size} onChange={(e)=>setSize(e.target.value)}>
                      <option value="">Select size</option>
                      {displayItem.available_sizes.map((opt, idx) => (
                        <option key={`${opt}-${idx}`} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder="e.g. S, M, L, XL"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                    />
                  )}
                </div>
                <div className="selector">
                  <label>Color {Array.isArray(displayItem?.available_colors) && displayItem.available_colors.length ? '' : '(optional)'}</label>
                  {Array.isArray(displayItem?.available_colors) && displayItem.available_colors.length ? (
                    <select value={color} onChange={(e)=>setColor(e.target.value)}>
                      <option value="">Select color</option>
                      {displayItem.available_colors.map((opt, idx) => (
                        <option key={`${opt}-${idx}`} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder="e.g. Red, Blue"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                    />
                  )}
                </div>
              </div>

              <div className="totals" style={{ marginTop: 12 }}>
                <div className="row">
                  <div>Items</div>
                  <div>
                    ₹ {(Number(displayItem.product_price) * effectiveQty).toFixed(2)}/-
                  </div>
                </div>
                <div className="row">
                  <div>Shipping Charges</div>
                  <div>₹ {Number(0).toFixed(2)}/-</div>
                </div>
                <div className="row" style={{ fontWeight: 700 }}>
                  <div>Order Total</div>
                  <div>
                    ₹ {(Number(displayItem.product_price) * effectiveQty).toFixed(2)}/-
                  </div>
                </div>
              </div>

              <button
                className="primary block"
                type="button"
                disabled={!selectedAddressId || !hasItem || addressSaving || loadingAddresses || productLoading}
                onClick={proceedToPayment}
                style={{ marginTop: 12 }}
              >
                {addressSaving || loadingAddresses || productLoading ? 'Please wait…' : 'Continue to Payment'}
              </button>
              {!loadingAddresses && addresses.length === 0 && (
                <div style={{ marginTop: 8, color: '#6b7280' }}>
                  Tip: add an address from your profile to proceed.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Address</h3>
              <button className="icon-btn" onClick={() => setShowAddModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="modal-body" onSubmit={handleAddAddress}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name*</label>
                  <input value={newAddress.name} onChange={(e)=>setNewAddress({...newAddress, name: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Mobile*</label>
                  <input value={newAddress.mobileNo} onChange={(e)=>setNewAddress({...newAddress, mobileNo: e.target.value.replace(/\D/g,'').slice(0,10)})} maxLength={10} />
                </div>
                <div className="form-group">
                  <label>Alt Mobile</label>
                  <input value={newAddress.alternateMobileNo} onChange={(e)=>setNewAddress({...newAddress, alternateMobileNo: e.target.value.replace(/\D/g,'').slice(0,10)})} maxLength={10} />
                </div>
              </div>
              <div className="form-group">
                <label>Full Address*</label>
                <textarea rows={2} value={newAddress.fullAddress} onChange={(e)=>setNewAddress({...newAddress, fullAddress: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Locality</label>
                  <input value={newAddress.locality} onChange={(e)=>setNewAddress({...newAddress, locality: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Landmark</label>
                  <input value={newAddress.landmark} onChange={(e)=>setNewAddress({...newAddress, landmark: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City*</label>
                  <input value={newAddress.city} onChange={(e)=>setNewAddress({...newAddress, city: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input value={newAddress.state} onChange={(e)=>setNewAddress({...newAddress, state: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Pincode*</label>
                  <input value={newAddress.pincode} onChange={(e)=>setNewAddress({...newAddress, pincode: e.target.value.replace(/\D/g,'').slice(0,6)})} maxLength={6} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select value={newAddress.type} onChange={(e)=>setNewAddress({...newAddress, type: e.target.value})}>
                    <option>Home</option>
                    <option>Work</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="primary" disabled={addingAddress}>{addingAddress ? 'Saving…' : 'Save Address'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
